import { GetProjectIdService } from '@/app/services/getProjectId.service';
import { ProjectsService } from '@/app/services/projects/projects.service';
import { DocumentService } from '@/app/services/shared/document.service';
import { DrawingService } from '@/app/services/shared/drawing.service';
import { ProgressService } from '@/app/services/shared/progress.service';
import { GetFileIconUtilityService } from '@/app/services/utility/get-file-icon-utility.service';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatError, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { NgToastService } from 'ng-angular-popup';

@Component({
  selector: 'app-drawings-dialog',
  standalone: true,
  providers: [provideNativeDateAdapter()],
  imports: [
    MatDialogModule,
    MatButtonModule,
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatSelectModule,
    MatIconModule,
    MatError
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './drawings-dialog.component.html',
  styleUrl: './drawings-dialog.component.css',
})
export class DrawingsDialogComponent implements OnInit {
  isEditing = false; // Track if we are in edit mode
  newFileName = ''; // Store the new filename

  documentForm: any = FormGroup;
  selectedFiles: File[] = [];
  isDragging = false;
  files: any[] = [];
  form: any;
  document_type: any;
  project_id: any;
  showSpinner = true;

  searchText: string = '';
  sets: any;
  filteredSets: any[] = [];
  allSets: any;
  selectedSet: any; // Selected set

  documentLength = 0;
  documentName = '';
  stage3Progress: any;
  isExpanded = false;

  selectedLanguage = new FormControl('en');

  defaultDrawingDate = new FormControl(null,Validators.required);
  publishDrawingDate = new FormControl(null,Validators.required);
  setDate = new FormControl('');
  revisions = new FormControl(null);
  containRevisions = new FormControl(false);
  getFromFilename = new FormControl(false);

  constructor(
    private documentService: DocumentService,
    private projectsService: ProjectsService,
    private toast: NgToastService,
    private drawingService: DrawingService,
    private getProjectIdService: GetProjectIdService,
    private progressService: ProgressService,
    private getFileIconService: GetFileIconUtilityService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}
  
  languages = [
    { value: 'en', viewValue: 'English' },
    { value: 'es', viewValue: 'Spanish' },
    { value: 'fr', viewValue: 'French' },
    { value: 'de', viewValue: 'German' },
    { value: 'zh', viewValue: 'Chinese' },
  ];
  // Function to handle changes in the search input
  filterSets(): void {
    if (this.searchText) {
      // Filter the sets based on the search term
      this.filteredSets = this.sets.filter((set) =>
        set.toLowerCase().includes(this.searchText.toLowerCase())
      );
    } else {
      this.filteredSets = [...this.sets]; // Reset to all sets when search is cleared
    }
  }
  addNewSets(set_name: any, project_id: any) {
    if (!set_name.trim()) return;

      const payload = {
      project_id: project_id,
      set_name: set_name
    };
  
    this.projectsService.addCustomSet(payload).subscribe({
      next: (response) => {
        if (response) {
          console.log('Set added successfully', response);
          this.sets.push({ set_name: response?.data?.set_name }); // Ensure it's an object
          this.filteredSets = [...this.sets];
          this.selectedSet = response?.data?.set_name;
          this.drawingSet();
        }
      },
      error: (err) => {
        console.error('Error adding set', err);
      },
    });
  }
  // Handle selection change if needed
  onSelectionChange(event: any): void {
    this.selectedSet = event.value; // Ensure selectedSet stores only the set ID
    console.log('Selected Set ID:', this.selectedSet);
  }
  
  toggleAdvancedOptions() {
    this.isExpanded = !this.isExpanded;
  }
  clearSelection(): void {
    this.selectedSet = null;
    this.searchText = '';
    this.filteredSets = [...this.sets]; // Reset options
  }

  ngOnInit(): void {
    this.progressService.progress3$.subscribe((value) => {
      this.stage3Progress = value;
    });

    this.project_id = this.getProjectIdService.getProjectId();
    this.document_type = this.data?.documentType;

    // Load the filename from local storage when the component initializes
    const savedFileName = localStorage.getItem('filename');
    if (savedFileName) {
      this.file.name = savedFileName; // Set filename from local storage
    }

    this.documentService.documentLength$.subscribe((length) => {
      this.documentLength = length;
    });

    this.documentService.documentName$.subscribe((name) => {
      this.documentName = name;
    });
    this.drawingSet();
  }

  drawingSet() {
    this.projectsService.drawingSet(this.project_id).subscribe({
      next: (response: any) => {
        this.sets = response.data; // Store the response data in the `sets` array
      },
      error: (error) => {
        console.error('API call failed:', error);
      },
    });
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
    if (event.dataTransfer?.files) {
      this.handleFiles(event.dataTransfer.files);
    }
  }

  lastUploadedFileName = ''; // Declare a variable to store the last uploaded file name

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.handleFiles(input.files);
    }
  }

  handleFiles(fileList: FileList) {
    for (let i = 0; i < fileList.length; i++) {
      this.files = [...this.files, fileList[i]]; // Update reference for change detection
    }

    // Save the last uploaded file name
    if (fileList.length > 0) {
      this.lastUploadedFileName = fileList[fileList.length - 1].name;
    }
  }
  dateFormatter = (date: any) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0'); // Month is 0-based
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  uploadFile() {
    if (this.defaultDrawingDate.invalid || this.publishDrawingDate.invalid || !this.selectedSet) {
      // Mark all fields as touched to trigger error messages
      this.defaultDrawingDate.markAsTouched();
      
      // Show error toast
      this.toast.warning('Please fill all required fields', 'WARNING', 4000);
      return;
    }
    if (this.files.length > 0 ) {
      const dataToSubmit = new FormData();
      dataToSubmit.append('document_title', this.lastUploadedFileName);
      dataToSubmit.append(
        'project_id',
        // '1'
        this.project_id
      );

      this.files.forEach((uploadedFile) => {
        dataToSubmit.append('document_documents[]', uploadedFile);
      });

      dataToSubmit.append('document_status', 'pending');

      dataToSubmit.append('document_user_doc_no', 'test');
      dataToSubmit.append('document_type', this.document_type);
      // Append selected set_name if available

      dataToSubmit.append('drawing_set_id', String(this.selectedSet.id));
      dataToSubmit.append('set_name', String(this.selectedSet.set_name));
      dataToSubmit.append('file_type', String(this.document_type));
      dataToSubmit.append('type', String(this.document_type));
      // Format and append dates
      const formattedDefaultDate = this.dateFormatter(
        this.defaultDrawingDate.value
      );
      const formattedPublishDate = this.dateFormatter(
        this.publishDrawingDate.value
      );
      const formattSetDate = this.dateFormatter(
        this.setDate.value
      );
      dataToSubmit.append('default_date', formattedDefaultDate);
      dataToSubmit.append('publish_date', formattedPublishDate);
      dataToSubmit.append('set_date', formattSetDate);
      dataToSubmit.append('revision', this.revisions.value || '');

      // Append checkboxes values
      dataToSubmit.append(
        'contain_revision',
        this.containRevisions.value ? '1' : '0'
      );
      dataToSubmit.append('file_name', this.getFromFilename.value ? '1' : '0');
      dataToSubmit.append('language', this.selectedLanguage.value);
      
      this.projectsService.addDesignDocument(dataToSubmit).subscribe({
        next: () => {
          const message = `${this.document_type} Added Successfully`;
          const capitalizedMessage = message.charAt(0).toUpperCase() + message.slice(1);
      
          this.toast.success(
            capitalizedMessage,
            'SUCCESS',
            4000
          );
          if (this.documentLength == 0) {
            const progress = this.stage3Progress + 100 / 7;
            this.progressService.updateProgress3(progress);
          }
          this.drawingService.notifyDrawingUpdate();
        },
        error: (error: any) => {
          console.error('Login failed:', error);
          this.toast.danger(error.error.message, 'DANGER', 4000);
        },
      });
    }
  }

  removeFile(index: number) {
    this.files.splice(index, 1);
  }

  file = {
    name: 'example.txt', // Sample filename
  };

  startEditing(index: number) {
    this.newFileName = this.file.name; // Set input value to current filename
    this.isEditing = true; // Switch to edit mode

    console.log('The pass index is:', index);
    if (index >= 0 && index < this.files.length) {
      console.log('the pass file element is:', this.files[index]);
    } else {
      console.log('Index out of bounds');
    }
  }

  saveFileName() {
    this.file.name = this.newFileName; // Update filename
    localStorage.setItem('filename', this.file.name); // Save to local storage
    this.isEditing = false; // Exit edit mode
  }

  cancelEditing() {
    this.isEditing = false; // Exit edit mode without saving
  }
  getFileIcon(file: {
    file: File;
    preview: string;
    id?: number;
    name?: string;
  }): string {
    return this.getFileIconService.getFileIcon(file);
  }
}
